import { useQuery } from '@tanstack/react-query';
import {
  CubeIcon,
  UsersIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import dashboardService from '@services/dashboardService';
import productService from '@services/productService';
import { PageMeta, FormShimmer, Card } from '@common/components';
import { Badge, StatTile } from '@common/components/ui';
import { Order, OrderStatus } from '@services/core/types';
import { ORDER_STATUS_BADGE } from '@/config/orderStatus';

const getStatusBadgeColor = (status: OrderStatus): 'success' | 'error' | 'info' | 'warning' | 'gray' =>
  ORDER_STATUS_BADGE[status]?.color || 'gray';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const Dashboard = () => {
  const { data: summary, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => (await dashboardService.getSummary()).data,
  });

  // Lightweight standalone query — total product count isn't part of the
  // combined dashboard payload since it's cheap and orthogonal to order/user stats.
  const { data: productData, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['product-count'],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('limit', '1');
      return productService.getAll(params);
    },
  });

  const isLoadingStats = isLoading || isLoadingProducts;
  const orderStats = summary?.orderStats;
  const userStats = summary?.userStats;
  const revenueTrend = summary?.revenueTrend;
  const recentOrders = summary?.recentOrders ?? [];

  const revenueChartOptions: ApexOptions = {
    chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false } },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2 },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1 } },
    xaxis: {
      categories: revenueTrend?.labels?.length ? revenueTrend.labels : ['No Data'],
      labels: { style: { colors: '#64748b' } },
    },
    yaxis: {
      labels: { style: { colors: '#64748b' }, formatter: (value) => `€${value.toFixed(0)}` },
    },
    colors: ['#4f46e5'],
    grid: { borderColor: '#e2e8f0' },
    tooltip: { y: { formatter: (value) => `€${value.toFixed(2)}` } },
    legend: { show: true, position: 'top', horizontalAlign: 'left' },
  };

  const revenueChartSeries = [
    { name: 'Revenue', data: revenueTrend?.data?.length ? revenueTrend.data : [0] },
  ];

  const orderStatusChartOptions: ApexOptions = {
    chart: { type: 'donut' },
    labels: ['Pending', 'Processing', 'Shipped', 'Delivered', 'Cancelled'],
    colors: ['#f59e0b', '#8b5cf6', '#06b6d4', '#10b981', '#ef4444'],
    legend: { position: 'bottom', show: true, markers: { size: 6 } },
    dataLabels: { enabled: true, formatter: (val: number) => `${val.toFixed(0)}%` },
    plotOptions: { pie: { donut: { labels: { show: false } } } },
    responsive: [{ breakpoint: 480, options: { legend: { position: 'bottom' } } }],
  };

  const orderStatusChartSeries = orderStats
    ? [
        orderStats.pendingOrders || 0,
        orderStats.processingOrders || 0,
        orderStats.shippedOrders || 0,
        orderStats.deliveredOrders || 0,
        orderStats.cancelledOrders || 0,
      ]
    : [1, 0, 0, 0, 0];
  const hasOrderStatusData = orderStatusChartSeries.some((val) => val > 0);

  return (
    <>
      <PageMeta title="Dashboard" breadcrumbs={[{ label: 'Dashboard' }]} />

      <div className="space-y-6">
        {isLoadingStats ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <FormShimmer key={i} />
            ))}
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label="Total Products"
                value={productData?.data.total || 0}
                icon={<CubeIcon />}
                iconClassName="bg-blue-500"
                href="/products"
              />
              <StatTile
                label="Total Users"
                value={(userStats?.total || 0).toLocaleString()}
                icon={<UsersIcon />}
                iconClassName="bg-green-500"
                href="/users"
              />
              <StatTile
                label="Total Orders"
                value={(orderStats?.totalOrders || 0).toLocaleString()}
                icon={<ShoppingBagIcon />}
                iconClassName="bg-purple-500"
                href="/orders"
              />
              <StatTile
                label="Total Revenue"
                value={formatCurrency(orderStats?.totalRevenue || 0)}
                icon={<CurrencyDollarIcon />}
                iconClassName="bg-orange-500"
              />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card padded={false}>
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Revenue Trend</h3>
                </div>
                <div className="p-6">
                  {revenueTrend?.data?.length ? (
                    <Chart options={revenueChartOptions} series={revenueChartSeries} type="area" height={300} />
                  ) : (
                    <div className="flex items-center justify-center h-[300px] text-gray-500 dark:text-gray-400">
                      No revenue data available
                    </div>
                  )}
                </div>
              </Card>

              <Card padded={false}>
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Order Status Distribution</h3>
                </div>
                <div className="p-6">
                  {hasOrderStatusData ? (
                    <Chart options={orderStatusChartOptions} series={orderStatusChartSeries} type="donut" height={300} />
                  ) : (
                    <div className="flex items-center justify-center h-[300px] text-gray-500 dark:text-gray-400">
                      No order data available
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Recent Orders */}
            <Card padded={false}>
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Recent Orders</h3>
              </div>
              <div className="overflow-x-auto">
                {recentOrders.length === 0 ? (
                  <div className="p-8 text-center text-gray-500 dark:text-gray-400">No recent orders</div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Order Number
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {recentOrders.map((order: Order) => (
                        <tr key={order.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            #{order.orderNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(order.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {formatCurrency(order.total)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <Badge color={getStatusBadgeColor(order.status)}>{order.status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </Card>
          </>
        )}
      </div>
    </>
  );
};

export default Dashboard;
